"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  MAX_HISTORY_YEARS,
  getAnalyticsPeriodOptions,
  type AnalyticsPeriod,
} from "@/lib/analytics/period";
import type { DateRange } from "react-day-picker";
import { useSharedUiTranslations, useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


export interface PeriodSelectorProps<TPeriod extends string = AnalyticsPeriod> {
  period: TPeriod | "custom";
  customRange?: { from: string; to: string } | null;
  onPeriodChange: (
    period: TPeriod | "custom",
    customRange?: { from: string; to: string },
  ) => void;
  /** Defaults to the shared set; pass a narrower one where a dashboard supports fewer. */
  options?: { value: TPeriod; label: string }[];
  /** Some reports support an inclusive range containing just one day. */
  allowSingleDay?: boolean;
  className?: string;
  /** The dropdown itself, which the wrapper's width does not reach. */
  triggerClassName?: string;
}

export function PeriodSelector<TPeriod extends string = AnalyticsPeriod>({
  period,
  customRange,
  onPeriodChange,
  options,
  allowSingleDay = false,
  className,
  triggerClassName = "min-w-0 flex-1 sm:w-[180px] sm:flex-none",
}: PeriodSelectorProps<TPeriod>) {
  const tUi = useSharedUiTranslations("PeriodSelector")

  const {locale} = useUiControlLabels()
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>();
  const [numberOfMonths, setNumberOfMonths] = useState(1);

  useEffect(() => {
    const compact = window.matchMedia("(max-width: 639px), (max-height: 600px)");
    const updateMonths = () => setNumberOfMonths(compact.matches ? 1 : 2);
    updateMonths();
    compact.addEventListener("change", updateMonths);
    return () => compact.removeEventListener("change", updateMonths);
  }, []);

  // "custom" is the calendar button beside the dropdown, not an item inside it.
  const presetOptions = useMemo(() => (options ?? getAnalyticsPeriodOptions(tUi)).filter((o) => o.value !== "custom"), [options, tUi]);

  const today = useMemo(() => new Date(), []);
  const minDate = useMemo(
    () =>
      new Date(
        today.getFullYear() - MAX_HISTORY_YEARS,
        today.getMonth(),
        today.getDate(),
      ),
    [today],
  );

  const selectValue = period === "custom" ? "__custom__" : period;
  const selectedLabel = period === "custom" && customRange
    ? formatDateLabel(customRange.from, customRange.to, locale)
    : presetOptions.find((option) => option.value === period)?.label;

  const handlePresetChange = useCallback(
    (value: string) => {
      if (value === "__custom__") return;
      onPeriodChange(value as TPeriod);
    },
    [onPeriodChange],
  );

  const handleApply = useCallback(() => {
    if (!pendingRange?.from || !pendingRange?.to) return;
    const from = format(pendingRange.from, "yyyy-MM-dd");
    const to = format(pendingRange.to, "yyyy-MM-dd");
    onPeriodChange("custom", { from, to });
    setCalendarOpen(false);
    setPendingRange(undefined);
  }, [pendingRange, onPeriodChange]);

  const handleCalendarOpenChange = useCallback(
    (open: boolean) => {
      setCalendarOpen(open);
      if (!open) {
        setPendingRange(undefined);
      }
    },
    [],
  );

  const calendarSelected = useMemo<DateRange | undefined>(() => {
    if (pendingRange) return pendingRange;
    if (period === "custom" && customRange) {
      return {
        from: new Date(customRange.from + "T00:00:00"),
        to: new Date(customRange.to + "T00:00:00"),
      };
    }
    return undefined;
  }, [pendingRange, period, customRange]);

  const canApply =
    pendingRange?.from != null &&
    pendingRange?.to != null &&
    (allowSingleDay || pendingRange.from.getTime() !== pendingRange.to.getTime());

  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      {/* Preset dropdown — always visible */}
      <Select value={selectValue} onValueChange={handlePresetChange}>
        <SelectTrigger className={triggerClassName} aria-label={tUi("period")}>
          <span>{selectedLabel}</span>
        </SelectTrigger>
        <SelectContent>
          {presetOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
          {period === "custom" && customRange && (
            <SelectItem value="__custom__">
              {formatDateLabel(customRange.from, customRange.to, locale)}
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      {/* Calendar button — opens range picker popover */}
      <Popover open={calendarOpen} onOpenChange={handleCalendarOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant={period === "custom" ? "default" : "outline"}
            size="icon"
            className="shrink-0"
            aria-label={tUi("chooseCustomDateRange")}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="flex max-h-[var(--radix-popover-content-available-height)] w-auto max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0"
          align="end"
          collisionPadding={16}
        >
          <div className="min-h-0 overflow-y-auto overscroll-contain">
          <Calendar
            mode="range"
            selected={calendarSelected}
            onSelect={setPendingRange}
            disabled={{ before: minDate, after: today }}
            numberOfMonths={numberOfMonths}
            defaultMonth={new Date(
              (calendarSelected?.from ?? today).getFullYear(),
              (calendarSelected?.from ?? today).getMonth() - (numberOfMonths - 1),
            )}
          />
          </div>
          <div className="flex shrink-0 items-center justify-between gap-3 border-t px-3 py-2">
            <p className="min-w-0 text-xs text-muted-foreground" aria-live="polite">
              {formatPendingRange(pendingRange, tUi, locale)}
            </p>
            <Button size="sm" className="shrink-0" disabled={!canApply} onClick={handleApply}>
              <span key={tUi("apply")}>{tUi("apply")}</span></Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function formatDateLabel(from: string, to: string, locale: string): string {
  const start = new Date(from + "T00:00:00")
  const end = new Date(to + "T00:00:00")
  const startFormat = new Intl.DateTimeFormat(locale, {month: "short", day: "numeric"})
  const endFormat = new Intl.DateTimeFormat(locale, {month: "short", day: "numeric", year: "numeric"})
  return `${startFormat.format(start)} – ${endFormat.format(end)}`
}

function formatPendingRange(range: DateRange | undefined, t: ReturnType<typeof useSharedUiTranslations<"PeriodSelector">>, locale: string): string {
  if (!range?.from) return t("selectDates")
  if (!range.to) return `${new Intl.DateTimeFormat(locale, {month: "short", day: "numeric", year: "numeric"}).format(range.from)} – ...`
  return formatDateLabel(format(range.from, "yyyy-MM-dd"), format(range.to, "yyyy-MM-dd"), locale)
}
